import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Brain, Check, Trash2 } from 'lucide-react';
import { type MemoryDurability, type MemoryScope } from '@apm/contracts';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { ErrorState, Skeleton } from '../../../components/ui/states';
import { errorMessage } from '../../../lib/error-message';
import { aiApi, aiKeys } from '../api';

/**
 * Ghi chú bộ nhớ AI — APM-H-j. Một panel, hai chỗ: trang dự án (`scope=project`)
 * và trang cá nhân (`scope=user`). Danh sách + xoá từng dòng + thêm tay + "vẫn
 * đúng" (tăng `confirmations`). Không có ghi nhớ ngầm: mọi dòng ở đây do người bấm tạo.
 */
export function MemoryNotesPanel({
  scope,
  scopeId,
}: {
  scope: MemoryScope;
  scopeId?: string;
}): ReactNode {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [text, setText] = useState('');
  const [durability, setDurability] = useState<MemoryDurability>('durable');

  const notes = useQuery({
    queryKey: aiKeys.memoryNotes(scope, scopeId),
    queryFn: () => aiApi.memoryNotes(scope, scopeId),
  });
  const invalidate = () =>
    void queryClient.invalidateQueries({ queryKey: aiKeys.memoryNotes(scope, scopeId) });

  const create = useMutation({
    mutationFn: () =>
      aiApi.createMemoryNote({
        scope,
        ...(scopeId ? { scopeId } : {}),
        text: text.trim(),
        durability,
        origin: 'manual',
      }),
    onSuccess: () => {
      setText('');
      toast.success(t('aiMemory.saved'));
      invalidate();
    },
    onError: (error) => toast.error(errorMessage(error, t)),
  });
  const remove = useMutation({
    mutationFn: (id: string) => aiApi.deleteMemoryNote(id),
    onSuccess: invalidate,
    onError: (error) => toast.error(errorMessage(error, t)),
  });
  const confirm = useMutation({
    mutationFn: (id: string) => aiApi.confirmMemoryNote(id),
    onSuccess: invalidate,
    onError: (error) => toast.error(errorMessage(error, t)),
  });

  if (notes.isPending) return <Skeleton className="h-24" />;
  if (notes.isError)
    return (
      <ErrorState message={errorMessage(notes.error, t)} onRetry={() => void notes.refetch()} />
    );

  return (
    <div className="flex flex-col gap-3">
      <p className="text-sm text-muted-foreground">{t(`aiMemory.help.${scope}`)}</p>

      {notes.data.length === 0 ? (
        <p className="text-sm text-muted-foreground">{t('aiMemory.empty')}</p>
      ) : (
        <ul className="flex flex-col gap-2">
          {notes.data.map((note) => (
            <li
              key={note.id}
              className="flex items-start justify-between gap-2 rounded-DEFAULT border border-border p-2 text-sm"
            >
              <div className="min-w-0 flex-1">
                <p>{note.text}</p>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  {t(`aiMemory.origin.${note.origin}`)} ·{' '}
                  {t(`aiMemory.durability.${note.durability}`)}
                  {note.confirmations > 0
                    ? ` · ${t('aiMemory.confirmed', { count: note.confirmations })}`
                    : ''}
                </p>
              </div>
              <div className="flex shrink-0 items-center gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  title={t('aiMemory.stillTrue')}
                  aria-label={t('aiMemory.stillTrue')}
                  disabled={confirm.isPending}
                  onClick={() => confirm.mutate(note.id)}
                >
                  <Check className="size-4" aria-hidden />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  title={t('common.delete')}
                  aria-label={t('common.delete')}
                  disabled={remove.isPending}
                  onClick={() => remove.mutate(note.id)}
                >
                  <Trash2 className="size-4" aria-hidden />
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}

      <form
        className="flex flex-wrap items-center gap-2"
        onSubmit={(event) => {
          event.preventDefault();
          if (text.trim().length >= 5) create.mutate();
        }}
      >
        <Input
          className="min-w-0 flex-1"
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={t('aiMemory.placeholder')}
          maxLength={500}
        />
        <select
          className="h-9 rounded-DEFAULT border border-border bg-background px-2 text-sm"
          value={durability}
          onChange={(e) => setDurability(e.target.value as MemoryDurability)}
        >
          <option value="durable">{t('aiMemory.durability.durable')}</option>
          <option value="temporary">{t('aiMemory.durability.temporary')}</option>
        </select>
        <Button type="submit" size="sm" disabled={create.isPending || text.trim().length < 5}>
          <Brain className="mr-1 size-4" aria-hidden />
          {t('aiMemory.add')}
        </Button>
      </form>
    </div>
  );
}

/**
 * "Ghi nhớ điều này?" — nút nhỏ hiện sau khi trả lời hỏi lại hay từ chối gợi ý.
 * AI không tự ghi; người bấm mới lưu (`origin` nói câu này rút từ đâu).
 */
export function RememberNoteButton({
  projectId,
  text,
  origin,
  sourceRunId,
}: {
  projectId: string;
  text: string;
  origin: 'answer' | 'rejection';
  sourceRunId?: string;
}): ReactNode {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [saved, setSaved] = useState(false);
  const save = useMutation({
    mutationFn: () =>
      aiApi.createMemoryNote({
        scope: 'project',
        scopeId: projectId,
        text: text.trim().slice(0, 500),
        durability: 'durable',
        origin,
        ...(sourceRunId ? { sourceRunId } : {}),
      }),
    onSuccess: () => {
      setSaved(true);
      toast.success(t('aiMemory.saved'));
      void queryClient.invalidateQueries({ queryKey: aiKeys.memoryNotes('project', projectId) });
    },
    onError: (error) => toast.error(errorMessage(error, t)),
  });
  if (text.trim().length < 5) return null;
  if (saved)
    return <span className="text-xs text-muted-foreground">{t('aiMemory.remembered')}</span>;
  return (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      disabled={save.isPending}
      onClick={() => save.mutate()}
    >
      <Brain className="mr-1 size-4" aria-hidden />
      {t('aiMemory.rememberThis')}
    </Button>
  );
}
